// purpose: opening sequence (opening-screen-sheet 01→04) as a DOM/CSS overlay.
// Plays once per page load, is skippable from t=0 and is inert under
// prefers-reduced-motion. The renderer is never touched: the "world waking"
// ramp is a backdrop-filter veil over the canvas, not a render-side change.
import { useEffect, useState } from 'react';
import { GameMode } from '../../game/constants';

export type IntroPhase = 'boot' | 'reveal' | 'title' | 'done';

/**
 * Module scope on purpose. TitlePanel and this overlay both unmount whenever
 * SETTINGS or CONTROLS opens, so component state would replay the sequence
 * every time the player came back to the title.
 */
let introConsumed = false;

/** Sheet 01 holds until the runtime is actually past Boot; these are floors. */
const BOOT_MS = 1500;
const REVEAL_MS = 1500;
const TITLE_MS = 1500;

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function initialPhase(): IntroPhase {
  return introConsumed || prefersReducedMotion() ? 'done' : 'boot';
}

/**
 * Drives the phase machine. `mode` gates boot→reveal so the sequence works
 * whether the runtime boot is instant or slower than the sheet's 0–3s window.
 */
export function useTitleIntro(mode: number): IntroPhase {
  const [phase, setPhase] = useState<IntroPhase>(initialPhase);
  const [bootFloorPassed, setBootFloorPassed] = useState(false);
  const runtimeReady = mode !== GameMode.Boot;

  useEffect(() => {
    if (phase !== 'boot') return;
    const timer = window.setTimeout(() => setBootFloorPassed(true), BOOT_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (phase === 'boot' && bootFloorPassed && runtimeReady) setPhase('reveal');
  }, [phase, bootFloorPassed, runtimeReady]);

  useEffect(() => {
    if (phase !== 'reveal' && phase !== 'title') return;
    const next: IntroPhase = phase === 'reveal' ? 'title' : 'done';
    const timer = window.setTimeout(() => setPhase(next), phase === 'reveal' ? REVEAL_MS : TITLE_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  // Skip is live from the first render, not from the first phase boundary.
  useEffect(() => {
    if (phase === 'done') {
      introConsumed = true;
      return;
    }
    const skip = (): void => setPhase('done');
    window.addEventListener('keydown', skip);
    window.addEventListener('pointerdown', skip);
    return () => {
      window.removeEventListener('keydown', skip);
      window.removeEventListener('pointerdown', skip);
    };
  }, [phase]);

  return phase;
}

/** Sheet 01 lockup. Shared so the Boot mode panel and the overlay never drift. */
export function BootLockup() {
  return (
    <div className="boot-lockup">
      <p className="boot-title">LUMINA ARCHIVE</p>
      <span className="boot-rule" aria-hidden="true" />
      <p className="boot-line">
        INITIALIZING MEMORY...<i className="boot-cursor" aria-hidden="true" />
      </p>
    </div>
  );
}

/**
 * Phases boot and reveal only: from `title` onward the title panel itself owns
 * the animation, so keeping an overlay mounted would just dim the backdrop.
 */
export function TitleIntro({ phase }: { phase: IntroPhase }) {
  if (phase !== 'boot' && phase !== 'reveal') return null;
  return (
    <div className={`intro-overlay intro-${phase}`} aria-hidden="true">
      <div className="intro-veil" />
      <div className="intro-bloom" />
      <BootLockup />
    </div>
  );
}
